import React from 'react';
import styled from 'styled-components';

import ObjectId from 'bson-objectid';

import AntCheckbox from 'antd/lib/checkbox';
import AntButton from 'antd/lib/button';

import Admin from 'hive-admin';
import Field from 'hive-admin/src/components/Field';
import FieldText from 'hive-admin/src/components/FieldText';

import { DownloadWarehouseSticker } from './WarehouseSticker';

import { renderCurrency } from '../../../components/SystemCountry';
import { getProductsUrl } from '../../../helpers/getStoreUrl';
import getFilterWithCol from '../../../helpers/getFilterWithCol';
import {
  getI18nFields,
} from '../../../helpers/i18n';

import { fieldVariationVariants } from './FieldVariationVariants';
import { fieldStocks } from './FieldStock';

import Types from '../../../modules/types';

import createSlug from '../../../helpers/createSlug';
import tests from '../../../helpers/tests';

import {
  productTypeNotMerchant,
  productKindIsAbstract,
  variationProductKindIsAbstract,
  // productTypeNotMerchantNorReseller,
} from './helpers';

const {
  viewerIsNotAdmin,
  activityIsNotEdit,
} = tests;

const storeStorageHasNoWarehouseSupport = props => {
  const mainData = props.datas && props.datas.length
    ? props.datas[props.datas.length - 1]
    : props.data;
  return !mainData?.storeData?.storageOptions?.includes(
    Types.CONSTANTS.ShippingStorage.WAREHOUSE
  );
};

const isNotShowingMore = [
  'condition.testFieldValue',
  'showMore',
  '!==',
  true,
];

const isVariantsEmpty = (props) => {
  const mainForm = props.forms && props.forms.length
    ? props.forms[props.forms.length - 1]
    : props.form;
  const variants = (
    mainForm?.getFieldValue('variants')
    || []
  );
  return !(variants.filter(variant => (
    variant?._id
    && variant.choices?.length > 0
  )).length > 0);
};

const getIsNotUsingVat = (props) => {
  const form = (
    props?.forms?.length
      ? props.forms[props.forms.length - 1]
      : props.form
  );
  // console.log('form:', form);
  return form?.getFieldValue?.('storeData')?.trn?.active !== true;
};

// eslint-disable-next-line no-unused-vars
export const getCalculatedPriceIncludingVat = (value, props) => {
  if (Number.isFinite(value)) {
    let systemCountry = props?.systemCountry;
    if (!systemCountry) {
      const form = (
        props?.forms?.length
          ? props.forms?.[props.forms.length - 1]
          : props?.form
      );
      systemCountry = form?.getFieldValue?.('storeData').systemCountry;
      if (!systemCountry) {
        systemCountry = (
          props?.data?.systemCountry
          || props?.data?.store?.systemCountry
          || props?.datas?.[0]?.systemCountry
        );
      }
    }
    let valueVAT = Types.VAT;
    if (systemCountry) {
      valueVAT = Types.getSystemCountry(systemCountry).VAT;
    }
    const vat = Types.getRoundedAmount(value * valueVAT);
    const includingVat = Types.getRoundedAmount(value + vat);
    return includingVat;
  }
  return undefined;
};

// eslint-disable-next-line no-unused-vars
export const getCalculatedPriceWithoutVat = (value, props) => {
  if (Number.isFinite(value)) {
    let systemCountry = props?.systemCountry;
    if (!systemCountry) {
      const form = (
        props?.forms?.length
          ? props.forms?.[props.forms.length - 1]
          : props?.form
      );
      systemCountry = form?.getFieldValue?.('storeData').systemCountry;
      if (!systemCountry) {
        systemCountry = (
          props?.data?.systemCountry
          || props?.data?.store?.systemCountry
          || props?.datas?.[0]?.systemCountry
        );
      }
    }
    let valueVAT = Types.VAT;
    if (systemCountry) {
      valueVAT = Types.getSystemCountry(systemCountry).VAT;
    }
    const vat = Types.getRoundedAmount((value / (1 + valueVAT)) * valueVAT);
    const withoutVat = Types.getRoundedAmount(value - vat);
    return withoutVat;
  }
  return undefined;
};

const ShowMoreButton = styled(AntButton)`
  margin-top: 10px;
  margin-bottom: 10px;
`;

class FieldTextVariationDefaultable extends FieldText {
  // eslint-disable-next-line arrow-body-style
  handleChange = (value) => {
    this.props.onChange(this.props.prepareValueForForm(value, this.props));
    const update = {};
    let shouldUpdate = false;
    let calculatedPrice;
    value = value?.target?.value;
    const isUsingVat = !getIsNotUsingVat(this.props);
    if (
      isUsingVat
      && this.props.calculatedPriceName
      && this.props.getCalculatedPrice
    ) {
      calculatedPrice = this.props.getCalculatedPrice(value, this.props);
      if (typeof calculatedPrice !== 'undefined') {
        shouldUpdate = true;
        update[this.props.calculatedPriceName] = calculatedPrice;
      }
    }
    if (this.props.overrideName) {
      const { overrideName, calculatedPriceOverrideName } = this.props;
      const form = (
        this.props.forms?.length
          ? this.props.forms[this.props.forms.length - 1]
          : this.props.form
      );
      const variations = form.getFieldValue('variations') || [];
      let didChange = false;
      const newVariations = variations.map((variation) => {
        const { overrides } = variation;
        if (!overrides || !overrides.includes(overrideName)) {
          didChange = true;
          const newVariation = {
            ...variation,
            [overrideName]: value,
          };
          if (
            isUsingVat
            && calculatedPriceOverrideName
            && typeof calculatedPrice !== 'undefined'
          ) {
            newVariation[calculatedPriceOverrideName] = calculatedPrice;
          }
          return newVariation;
        }
        return variation;
      });
      if (didChange) {
        shouldUpdate = true;
        update.variations = newVariations;
      }
    }
    if (shouldUpdate) {
      this.props.form.setFieldsValue(update);
    }
  }
}
Admin.addToLibrary(
  'FieldTextVariationDefaultable',
  config => FieldTextVariationDefaultable.create(config),
);

// eslint-disable-next-line max-len
class FieldTextVariationDefaultableOverridable extends FieldTextVariationDefaultable {
  renderInput(inputProps, ...rest) {
    const { form } = this.props;
    const overrides = form.getFieldValue('overrides') || [];
    const isOverriden = overrides.includes(inputProps.name);

    const renderedInput = super.renderInput(inputProps, ...rest);

    return React.cloneElement(
      renderedInput,
      {
        disabled: inputProps.disabled || !isOverriden,
        addonBefore: (
          <AntCheckbox
            size="small"
            checked={isOverriden}
            onChange={event => {
              const { name } = inputProps;
              const { calculatedPriceName } = this.props;
              const names = [name];
              if (calculatedPriceName) {
                names.push(calculatedPriceName);
              }
              const newIsOverriden = !!event.target.checked;
              const update = {
                overrides: (
                  newIsOverriden
                    ? [...overrides, ...names]
                    : overrides.filter(overrideName => (
                      !names.includes(overrideName)
                    ))
                ),
              };
              if (!newIsOverriden) {
                const mainForm = (
                  this.props.forms?.length
                    ? this.props.forms[this.props.forms.length - 1]
                    : this.props.form
                );
                if (this.props.getOverridenValue) {
                  const value = this.props.getOverridenValue(
                    this.props,
                    this,
                    form,
                    mainForm,
                  );
                  if (typeof value !== 'undefined') {
                    update[name] = value;
                  }
                } else {
                  const value = mainForm.getFieldValue(
                    `variationDefaults.${name}`,
                  );
                  if (typeof value !== 'undefined') {
                    update[name] = value;
                    if (calculatedPriceName) {
                      const valueCalculatedPrice = mainForm.getFieldValue(
                        `variationDefaults.${calculatedPriceName}`,
                      );
                      if (typeof valueCalculatedPrice !== 'undefined') {
                        update[calculatedPriceName] = valueCalculatedPrice;
                      }
                    }
                  }
                }
              }
              form.setFieldsValue(update);
            }}
          />
        ),
      },
    );
  }
}
Admin.addToLibrary(
  'FieldTextVariationDefaultableOverridable',
  config => FieldTextVariationDefaultableOverridable.create(config),
);

// export class VariationForm extends Form {
//   renderField(field, wrapperProps = {}) {
//     // console.log('fieldName:', field?.name, field);
//     return (
//       // <VariationInputWrapper key={field.name} {...wrapperProps}>
//       //   {super.renderField(field)}
//       // </VariationInputWrapper>
//       super.renderField(field)
//     );
//   }
// }

const formItemConfig = {
  // labelCol: { span: 0 },
  // wrapperCol: { offset: 0, span: 24 },
  style: { marginBottom: '3px' },
};

const getColForPriceField = (fieldConfig, props) => {
  const isNotUsingVat = getIsNotUsingVat(props);
  const col = isNotUsingVat ? 24 : 12;
  return {
    xs: { span: 24 },
    lg: { span: col },
  };
};

export const fieldImage = ['FieldUploadRefsHorizontal', {
  name: 'image',
  label: null,
  maxCount: 1,
  accept: 'image/jpg,image/jpeg,image/png,image/webp',
  listType: 'picture-small',
  uploadLabel: 'Upload Image',
  transformations: [
    ['GM', { command: 'compress', maxWidth: 2000 }],
    ['GM', { command: 'webp' }],
  ],
}];

// eslint-disable-next-line max-len
export const fieldStoreSkuDisabled = ['FieldTextVariationDefaultableOverridable', {
  name: 'storeSku',
  label: null,
  align: 'right',
  placeholder: 'Store-wide unique SKU',
  disabled: true,
  isVirtual: () => true,
  prefix: 'SKU:',
  rules: [['validation.isRequired']],
  formItemConfig,
  // eslint-disable-next-line max-len
  // hidden: [tests.viewerIsAdminAndDoesNotHavePermissionToEditProductsOtherCasesReversed],
}];

// eslint-disable-next-line max-len
export const fieldSlugDisabled = ['FieldTextVariationDefaultableOverridable', {
  name: 'slug',
  label: null,
  // eslint-disable-next-line max-len
  // hidden: [tests.viewerIsAdminAndDoesNotHavePermissionToEditProductsOtherCasesReversed],
  isVirtual: () => true,
  rules: [['validation.isRequired']],
  formItemConfig,
  copyLinkSegments: [
    (props) => {
      if (props.parentData && props.parentData.storeData) {
        return getProductsUrl(props.parentData.storeData);
      }
      return null;
    },
    () => {
      const input = document.body.querySelector('#slug');
      if (input) {
        return input.value;
      }
      return null;
    },
  ],
}];

export const fieldNameDisabled = ['FieldTextVariationDefaultableOverridable', {
  name: 'name',
  label: null,
  placeholder: 'Name',
  prefix: 'Name:',
  // eslint-disable-next-line max-len
  // hidden: [tests.viewerIsAdminAndDoesNotHavePermissionToEditProductsOtherCasesReversed],
  rules: [['validation.isRequired']],
  formItemConfig,
  i18nTranslatable: true,
  prepareValueForForm: (value, props) => {
    if (props.activity === 'create') {
      let i18nLanguageValue = null;
      const form = (
        props.forms
          ? props.forms[props.forms.length - 1]
          : props.form
      );
      if (form && form.getFieldValue) {
        i18nLanguageValue = form.getFieldValue('i18nLanguage') || 'en';
      } else {
        i18nLanguageValue = 'en';
      }
      if (i18nLanguageValue === 'en') {
        props.form.setFieldsValue({ slug: createSlug(value?.target?.value) });
      }
    }
    return value;
  },
}];
export const fieldStoreSku = ['FieldText', {
  name: 'storeSku',
  label: null,
  align: 'right',
  placeholder: 'Store-wide unique SKU',
  disabled: true,
  isVirtual: () => true,
  prefix: 'SKU:',
  rules: [['validation.isRequired']],
  formItemConfig,
  // eslint-disable-next-line max-len
  // hidden: [tests.viewerIsAdminAndDoesNotHavePermissionToEditProductsOtherCases],
}];
export const fieldSlug = ['FieldText', {
  name: 'slug',
  label: null,
  // eslint-disable-next-line max-len
  // hidden: [tests.viewerIsAdminAndDoesNotHavePermissionToEditProductsOtherCases],
  isVirtual: () => true,
  rules: [['validation.isRequired']],
  formItemConfig,
  copyLinkSegments: [
    (props) => {
      if (props.parentData && props.parentData.storeData) {
        return getProductsUrl(props.parentData.storeData);
      }
      return null;
    },
    () => {
      const input = document.body.querySelector('#slug');
      if (input) {
        return input.value;
      }
      return null;
    },
  ],
}];
export const fieldBarcodeDisabled = [
  'FieldTextVariationDefaultableOverridable', {
    name: 'barcode',
    label: null,
    align: 'right',
    placeholder: 'Barcode',
    prefix: 'Barcode:',
    sendEmpty: true,
    formItemConfig,
  }];
export const fieldBarcode = ['FieldText', {
  name: 'barcode',
  label: null,
  align: 'right',
  placeholder: 'Barcode',
  prefix: 'Barcode:',
  sendEmpty: true,
  formItemConfig,
}];

export const fieldName = ['FieldText', {
  name: 'name',
  label: null,
  placeholder: 'Name',
  prefix: 'Name:',
  // eslint-disable-next-line max-len
  // hidden: [tests.viewerIsAdminAndDoesNotHavePermissionToEditProductsOtherCases],
  rules: [['validation.isRequired']],
  formItemConfig,
  i18nTranslatable: true,
  prepareValueForForm: (value, props) => {
    if (props.activity === 'create') {
      let i18nLanguageValue = null;
      const form = (
        props.forms
          ? props.forms[props.forms.length - 1]
          : props.form
      );
      if (form && form.getFieldValue) {
        i18nLanguageValue = form.getFieldValue('i18nLanguage') || 'en';
      } else {
        i18nLanguageValue = 'en';
      }
      if (i18nLanguageValue === 'en') {
        props.form.setFieldsValue({ slug: createSlug(value?.target?.value) });
      }
    }
    return value;
  },
}];

export const fieldDescription = ['FieldTextArea', {
  name: 'description',
  label: null,
  // size: 'small',
  placeholder: 'Optional Description',
  formItemConfig,
  autoSize: {
    minRows: 2,
    maxRows: 6,
  },
  sendEmpty: true,
  i18nTranslatable: true,
}];

export const fieldShowMore = ['FieldReact', {
  name: 'showMore',
  virtual: [],
  initialValue: false,
  // eslint-disable-next-line arrow-body-style, no-unused-vars
  renderContent: (props, field) => {
    const active = !!props.value;
    const overridesCount = props.form.getFieldValue('overrides')?.length || 0;
    const overridesSuffix = (
      overridesCount > 0
        ? ` (${overridesCount})`
        : ''
    );
    return (
      <ShowMoreButton
        icon={active ? 'up' : 'down'}
        block
        onClick={() => props.onChange(!active)}
        type={active ? 'primary' : 'default'}
        // type="primary"
        style={{ marginBottom: active ? '10px' : '0px' }}
      >
        {`${active ? 'Hide Details' : 'Show Details'}${overridesSuffix}`}
      </ShowMoreButton>
    );
  },
}];

export const actionDownloadWarehouseSticker = ['Action', {
  name: 'downloadWarehouseSticker',
  title: 'Download Warehouse Sticker',
  virtual: [() => true],
  style: { width: '100%' },
  type: 'default',
  icon: 'download',
  renderAction: (props, action) => {
    const product = props.datas?.length
      ? props.datas[props.datas.length - 1]
      : props.data;
    const variation = props.data;
    return (
      <DownloadWarehouseSticker
        name={product.name}
        variation={variation.name}
        store={product.storeData.name}
        sku={`SKU - ${variation.storeSku}`}
        qrCode={`${process.env.REACT_APP_PACKMAN_SHOP_URL}/api/products/${product._id
          }/variations/${variation._id
          }/scan`}
        fileName={`Warehouse Sticker (${variation.storeSku}).pdf`}
      >
        {
          ({ loading }) => (
            loading
              ? action.renderAction({
                ...props,
                disabled: true,
              })
              : action.renderAction(props)
          )
        }
      </DownloadWarehouseSticker>
    );
  },
  skip: [
    activityIsNotEdit,
    viewerIsNotAdmin,
    productKindIsAbstract,
    storeStorageHasNoWarehouseSupport,
  ],
}];

export const [
  fieldWeight,
  fieldLength,
  fieldWidth,
  fieldHeight,

  fieldWarehouseRow,
  fieldWarehouseRack,
  fieldWarehouseShelf,
] = [
  ['weight', 'Weight', 'g', '0', true, {
    skip: [productKindIsAbstract],
    rules: [
      ['validation.isRequired'],
      ['validation.isNumber', { min: 0.1 }],
    ],
  }],
  ['length', 'Length', 'cm', '0', true, {
    skip: [productKindIsAbstract],
    rules: [
      ['validation.isRequired'],
      ['validation.isNumber', { min: 0.1 }],
    ],
  }],
  ['width', 'Width', 'cm', '0', true, {
    skip: [productKindIsAbstract],
    rules: [
      ['validation.isRequired'],
      ['validation.isNumber', { min: 0.1 }],
    ],
  }],
  ['height', 'Height', 'cm', '0', true, {
    skip: [productKindIsAbstract],
    rules: [
      ['validation.isRequired'],
      ['validation.isNumber', { min: 0.1 }],
    ],
  }],
  ...['Row', 'Rack', 'Shelf'].map(label => (
    [`warehouse${label}`, label, null, '-', false, {
      hidden: [
        viewerIsNotAdmin,
      ],
      virtual: [
        viewerIsNotAdmin,
      ],
      skip: [
        productKindIsAbstract,
        storeStorageHasNoWarehouseSupport,
      ],
      type: 'text',
      rules: [],
    }]
  )),
].map(([name, label, suffix, placeholder, required, extras = {}]) => [
  'FieldText', {
    name,
    label: null,
    placeholder,
    align: 'right',
    prefix: (
      suffix === '__SYSTEM_COUNTRY_CURRENCY__'
        ? renderCurrency({
          prefix: `${label} (`,
          suffix: ')',
        })
        : `${label}${suffix ? ` (${suffix})` : ''}:`
    ),
    type: 'number',
    rules: required
      ? [
        ['validation.isRequired'],
        ['validation.isNumber'],
      ]
      : [],
    formItemConfig,
    ...extras,
  },
]);

export const [
  fieldPrice,
  fieldPriceIncludingVat,
  fieldPriceBeforeSale,
  fieldPriceBeforeSaleIncludingVat,
] = [
  ['price', 'Price'],
  ['priceBeforeSale', 'Price Before Sale'],
].reduce(
  (agr, [namePrefix, addonBeforePrefix]) => {
    agr.push(
      ['FieldText', {
        name: `${namePrefix}IncludingVat`,
        calculatedPriceName: namePrefix,
        getCalculatedPrice: getCalculatedPriceWithoutVat,
        label: null,
        placeholder: '0',
        type: 'number',
        disabled: true,
        prefix: `${addonBeforePrefix} + VAT:`,
        rules: [
          ['validation.isRequired'],
          ['validation.isNumber'],
        ],
        skip: [getIsNotUsingVat],
        align: 'right',
        formItemConfig,
        getCol: getColForPriceField,
        isVirtual: () => true,
      }],
      ['FieldText', {
        name: namePrefix,
        calculatedPriceName: `${namePrefix}IncludingVat`,
        getCalculatedPrice: getCalculatedPriceIncludingVat,
        label: null,
        placeholder: '0',
        type: 'number',
        prefix: `${addonBeforePrefix}:`,
        rules: [
          ['validation.isRequired'],
          ['validation.isNumber'],
        ],
        align: 'right',
        formItemConfig,
        getCol: getColForPriceField,
      }],
    );
    return agr;
  },
  [],
);

export const FIELDS_VARIATION_DEFAULT = [
  ...[
    ...[
      fieldWeight,
      fieldLength,
      fieldWidth,
      fieldHeight,
    ].map(field => getFilterWithCol(field, 6)),
    fieldPrice,
    fieldPriceIncludingVat,
    fieldPriceBeforeSale,
    fieldPriceBeforeSaleIncludingVat,
    fieldBarcode,
    ...[
      fieldWarehouseRow,
      fieldWarehouseRack,
      fieldWarehouseShelf,
    ].map(field => getFilterWithCol(field, 8)),
  ].map(field => [
    (
      field[0] === 'FieldText'
        ? 'FieldTextVariationDefaultable'
        : field[0]
    ),
    {
      ...field[1],
      skip: [
        ...(field[1].skip || []),
        productTypeNotMerchant,
      ],
      name: `variationDefaults.${field[1].name}`,
      overrideName: field[1].name,
      calculatedPriceOverrideName: field[1].calculatedPriceName,
      calculatedPriceName: (
        field[1].calculatedPriceName
          ? `variationDefaults.${field[1].calculatedPriceName}`
          : undefined
      ),
    },
  ]),
  ['FieldReact', {
    name: 'variationDefaults.spacer',
    content: <div style={{ height: '0px' }} />,
  }],
];

export const fieldVariations = ['FieldSortableList', {
  name: 'variations',
  label: null,
  // supportDuplicate: true,
  addButtonLabel: 'Add SKU',
  skip: [productTypeNotMerchant],
  hidden: [
    ['condition.fieldIsNotTrue', 'showVariations'],
  ],
  // hidden: [productTypeNotMerchant],
  // virtual: [productTypeNotMerchant],
  condensed: true,
  getItemFields: getI18nFields,
  getInitialValue: (fieldConfig, formProps) => {
    const initialValue = Field.config.getInitialValue(fieldConfig, formProps);
    if (!initialValue) {
      return [{
        _id: `${ObjectId()}`,
      }];
    }
    return initialValue;
  },
  generateNewItem: (props, self, key, extras = {}) => {
    const form = (
      props.forms?.length
        ? props.forms[props.forms.length - 1]
        : props.form
    );
    const variationDefaults = [
      'weight',
      'width',
      'length',
      'height',
      'price',
      'priceIncludingVat',
      'priceBeforeSale',
      'priceBeforeSaleIncludingVat',
      'barcode',
      'warehouseRow',
      'warehouseRack',
      'warehouseShelf',
    ].reduce(
      (agr, name) => {
        try {
          const value = form.getFieldValue(`variationDefaults.${name}`);
          if (typeof value !== 'undefined') {
            agr[name] = value;
          }
        } catch (error) {
          //
        }
        return agr;
      },
      {
        overrides: [],
      },
    );
    return {
      _id: `${ObjectId()}`,
      ...variationDefaults,
      ...extras,
    };
  },
  fields: [
    ['FieldHidden', {
      name: 'overrides',
      initialValue: [],
    }],
    ...[
      fieldStoreSkuDisabled,
      fieldNameDisabled,
      fieldSlugDisabled,
      fieldBarcodeDisabled,
    ].map(field => getFilterWithCol(field, 8)),
    ...[
      fieldStoreSku,
      fieldName,
      fieldSlug,
      fieldBarcode,
    ].map(field => getFilterWithCol(field, 8)),
    [fieldVariationVariants[0], {
      ...fieldVariationVariants[1],
      hidden: [isVariantsEmpty],
    }],
    fieldStocks,
    fieldImage,
    fieldShowMore,
    ...[
      ...[
        ...[
          fieldWeight,
          fieldLength,
          fieldWidth,
          fieldHeight,
        ].map(field => getFilterWithCol([
          field[0],
          {
            ...field[1],
            skip: [variationProductKindIsAbstract],
          },
        ], 6)),
        fieldPrice,
        fieldPriceIncludingVat,
        fieldPriceBeforeSale,
        fieldPriceBeforeSaleIncludingVat,
        ...[
          fieldWarehouseRow,
          fieldWarehouseRack,
          fieldWarehouseShelf,
        ].map(field => getFilterWithCol(field, 8)),
      ].map(field => [
        (
          field[0] === 'FieldText'
            ? 'FieldTextVariationDefaultableOverridable'
            : field[0]
        ),
        field[1],
      ]),
      fieldDescription,
      [actionDownloadWarehouseSticker[0], {
        ...actionDownloadWarehouseSticker[1],
        skip: [
          ...((actionDownloadWarehouseSticker[1] || {}).skip || []),
          isNotShowingMore,
        ],
      }],
    ].map(field => [
      field[0],
      {
        ...field[1],
        hidden: [
          ...(field[1].hidden || []),
          isNotShowingMore,
        ],
      },
    ]),
  ],
}];
